import styled, { css } from 'styled-components';

import Icon, { type IconProps } from '#@/components/Icon';
import { COLOR, mixins, textStyles } from '#@/shared/utils/styles';

import type { CalloutSize, EnhancedCalloutType } from './types';
import { callOutTypeToColorMap } from './utils';

const getLineHeight = (size: CalloutSize) =>
  ({
    compact: 16, // equivalent to `bodySmall450`
    regular: 24, // equivalent to `bodyLarge450`
  })[size];

interface WrapperProps {
  $messageType: EnhancedCalloutType;
  $marginBottom: boolean | number;
  $size: CalloutSize;
}

export const Wrapper = styled.div<WrapperProps>`
  ${(props) =>
    ({
      compact: css`
        display: flex;
        flex-direction: column;
        gap: 15px;
        padding: 10px 15px;
        border-radius: 8px;
        background-color: ${mixins.rgba(callOutTypeToColorMap[props.$messageType], 0.05)};
        ${textStyles.bodySmall450};
        color: ${callOutTypeToColorMap[props.$messageType]};
      `,
      regular: css`
        display: flex;
        flex-direction: column;
        gap: 15px;
        padding: 15px;
        border-radius: 8px;
        background-color: ${mixins.rgba(callOutTypeToColorMap[props.$messageType], 0.05)};
        ${textStyles.bodyLarge450};
        color: ${COLOR.textPrimary};
      `,
    })[props.$size]}

  ${(props) =>
    props.$messageType === 'highlight' &&
    css`
      background-color: ${callOutTypeToColorMap[props.$messageType]};
      color: ${COLOR.white};
    `}

  ${(props) => {
    if (typeof props.$marginBottom === 'number') {
      return css`
        margin-bottom: ${props.$marginBottom}px;
      `;
    }
    if (props.$marginBottom) {
      return css`
        margin-bottom: 20px;
      `;
    }
    return undefined;
  }}
`;

interface InfoItemProps {
  $alignIcon: 'center' | 'top';
}

export const InfoItem = styled.div<InfoItemProps>`
  display: flex;
  align-items: ${(props) =>
    ({
      center: 'center',
      top: 'flex-start',
    })[props.$alignIcon]};
  gap: 12px;
`;

interface MessageProps {
  $allowOverflow: boolean;
}

export const Message = styled.div<MessageProps>`
  flex: 1;
  ${(props) =>
    !props.$allowOverflow &&
    css`
      overflow: hidden;
    `}
`;

interface IconWrapperProps {
  $size: CalloutSize;
}

export const IconWrapper = styled.div<IconWrapperProps>`
  display: flex;
  align-items: center;
  height: ${(props) => getLineHeight(props.$size)}px;
`;

interface StyledIconProps extends IconProps {
  $messageType: EnhancedCalloutType;
  $size: CalloutSize;
}

export const StyledIcon = styled(Icon)<StyledIconProps>`
  ${(props) =>
    ({
      compact: css`
        color: ${callOutTypeToColorMap[props.$messageType]};
      `,
      regular: css`
        color: ${callOutTypeToColorMap[props.$messageType]};
      `,
    })[props.$size]};

  ${(props) =>
    props.$messageType === 'highlight' &&
    css`
      color: ${COLOR.white};
    `}
`;
